import type {
  AdminAuthPresentationRead,
  AdminAuthPresentationSlotRead,
  AuthPresentationConfirmRequest,
  AuthPresentationSurface,
  AuthPresentationUploadUrlResponse,
  PublicAuthPresentationRead,
} from './generated/model'
import { apiUrl } from '../lib/api'

async function responseError(response: Response): Promise<Error> {
  const body = await response.json().catch(() => null)
  const detail = body?.detail
  const message =
    detail && typeof detail === 'object' && 'message' in detail
      ? String((detail as { message: unknown }).message)
      : typeof detail === 'string'
        ? detail
        : `Request failed: ${response.status}`
  return new Error(message)
}

export async function getPublicAuthPresentation(
  surface: AuthPresentationSurface,
): Promise<PublicAuthPresentationRead> {
  const response = await fetch(apiUrl(`/public/auth-presentation/${surface}`))
  if (!response.ok) throw await responseError(response)
  return response.json()
}

export async function getAdminAuthPresentation(): Promise<AdminAuthPresentationRead> {
  const response = await fetch(apiUrl('/admin/auth-presentation'))
  if (!response.ok) throw await responseError(response)
  return response.json()
}

export async function publishAuthPresentation(
  surface: AuthPresentationSurface,
  file: File,
  presentation: Pick<
    AuthPresentationConfirmRequest,
    'alt_text_en' | 'alt_text_tr' | 'focal_x' | 'focal_y'
  >,
): Promise<AdminAuthPresentationSlotRead> {
  const contentType = file.type.toLowerCase()
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(contentType)) {
    throw new Error('Only JPEG, PNG, and WebP login images are supported.')
  }

  const targetResponse = await fetch(
    apiUrl(`/admin/auth-presentation/${surface}/media/upload-url`),
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        original_filename: file.name,
        content_type: contentType,
        file_size: file.size,
      }),
    },
  )
  if (!targetResponse.ok) throw await responseError(targetResponse)
  const target = (await targetResponse.json()) as AuthPresentationUploadUrlResponse

  const uploadResponse = await fetch(target.upload_url, {
    method: 'PUT',
    headers: { 'content-type': contentType },
    body: file,
  })
  if (!uploadResponse.ok) {
    throw new Error(`Storage upload failed: ${uploadResponse.status}`)
  }

  const confirmResponse = await fetch(
    apiUrl(`/admin/auth-presentation/${surface}/media/confirm`),
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        provider_key: target.provider_key,
        original_filename: file.name,
        ...presentation,
      } satisfies AuthPresentationConfirmRequest),
    },
  )
  if (!confirmResponse.ok) throw await responseError(confirmResponse)
  return confirmResponse.json()
}

export async function restoreDefaultAuthPresentation(
  surface: AuthPresentationSurface,
): Promise<AdminAuthPresentationSlotRead> {
  const response = await fetch(
    apiUrl(`/admin/auth-presentation/${surface}/media`),
    { method: 'DELETE' },
  )
  if (!response.ok) throw await responseError(response)
  return response.json()
}
